import {
	Box,
	Dialog,
	DialogContentText,
	IconButton,
	Typography,
	useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CustomDialogTitle from './CustomDialogBox';
import sizeFontCustom from '../Discovery/fontSize.styles';
import { useTranslation } from '../../hooks/useTranslation';

DialogBox.defaultProps = {
	mobileNumber: '',
	boxPadding: '1.25rem 1.25rem 1.875rem 1.25rem',
	isTitleCentered: false,
	centeredSubTitle: false,
	subtitleXSFontSize: '0.75rem',
	subTitleMaxWidth: '80%',
	centeredChildren: false,
	titleMaxWidth: 'auto',
};

function DialogBox({
	open,
	handleClose,
	title,
	subTitle,
	mobileNumber,
	children,
	boxPadding,
	centeredText,
	subTitleMaxWidth,
	centeredChildren,
	marginTopChildren,
	customDetails,
	isConsentRenewal,
	isGrievance,
	showCloseButton = false,
	closeButtonVariant = 'primary',
	ariaLabelledBy,
	opaqueBackdrop = false,
	surfaceColor,
}) {
	const { t } = useTranslation();
	const theme = useTheme();
	const isDark =
		customDetails?.darkTheme === true || theme.palette.mode === 'dark';
	const showTitle = title ? true : false;
	const showStandaloneClose = !showTitle && showCloseButton && handleClose;
	const isNeutralClose = closeButtonVariant === 'neutral';
	const resolvedSurfaceColor =
		surfaceColor ||
		(isDark ? '#161616' : customDetails?.secondaryColor || '#FFFFFF');

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			disableEscapeKeyDown={false}
			{...(ariaLabelledBy ? { 'aria-labelledby': ariaLabelledBy } : {})}
			{...(opaqueBackdrop
				? {
						BackdropProps: {
							style: {
								backgroundColor: resolvedSurfaceColor,
								opacity: 1,
							},
							sx: {
								backgroundColor: `${resolvedSurfaceColor} !important`,
								opacity: '1 !important',
							},
						},
					}
				: {})}
			PaperProps={{
				elevation: opaqueBackdrop ? 8 : undefined,
				style: surfaceColor
					? {
							backgroundColor: resolvedSurfaceColor,
							opacity: 1,
							backgroundImage: 'none',
						}
					: undefined,
				sx: {
					borderRadius: '14px',
					...(surfaceColor
						? {
								backgroundColor: `${resolvedSurfaceColor} !important`,
								opacity: 1,
								backgroundImage: 'none',
							}
						: {
								backgroundColor: isConsentRenewal
									? isDark
										? '#161616'
										: customDetails?.secondaryColor || '#fff'
									: undefined,
							}),
				},
			}}
		>
			<Box
				style={
					surfaceColor ? { backgroundColor: resolvedSurfaceColor } : undefined
				}
				sx={{
					position: 'relative',
					padding: boxPadding,
					display: 'flex',
					flexDirection: 'column',
					alignItems: centeredChildren ? 'center' : '',
					borderRadius: '14px',
					maxWidth: '48rem',
					backgroundColor: surfaceColor
						? resolvedSurfaceColor
						: isConsentRenewal
							? isDark
								? '#161616'
								: customDetails?.secondaryColor || '#fff'
							: undefined,
				}}
			>
				{showStandaloneClose ? (
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: '0.25rem' }}>
						<IconButton
							size="small"
							aria-label={t('common.buttons.close')}
							onClick={handleClose}
							style={
								isNeutralClose
									? {
											color: isDark ? '#FFFFFF' : '#444444',
											backgroundColor: isDark ? '#2D2D2D' : '#F5F5F5',
										}
									: undefined
							}
							sx={{
								flexShrink: 0,
								p: '0.25rem',
								color: isNeutralClose
									? isDark
										? '#FFFFFF'
										: '#444444'
									: customDetails?.primaryColor ||
										theme.palette.primary.main,
								background: isNeutralClose
									? isDark
										? '#2D2D2D'
										: '#F5F5F5'
									: customDetails?.primaryColor
										? `${customDetails.primaryColor}20`
										: theme.palette.primary.light,
							}}
						>
							<CloseIcon sx={{ fontSize: '1.25rem' }} />
						</IconButton>
					</Box>
				) : null}
				{showTitle ? (
					<CustomDialogTitle
						onClose={handleClose}
						centeredText={centeredText}
						customDetails={customDetails}
						isConsentRenewal={isConsentRenewal}
						isGrievance={isGrievance}
					>
						{title}
						<DialogContentText
							sx={{
								mt: { xs: '0.5rem', md: '0.35rem' },
								fontSize: isConsentRenewal
									? '0.875rem'
									: { xs: '1rem', md: '0.875rem' },
								maxWidth: subTitleMaxWidth,
							}}
						>
							{subTitle}
							{mobileNumber && (
								<Typography
									sx={{
										display: 'inline',
										fontWeight: 700,
										color: '#111',
										...sizeFontCustom.subHeading,
									}}
								>
									{mobileNumber}
								</Typography>
							)}
						</DialogContentText>
					</CustomDialogTitle>
				) : null}
				<Box sx={{ marginTop: { xs: '0.25rem', md: `${marginTopChildren}` } }}>
					{children}
				</Box>
			</Box>
		</Dialog>
	);
}

export default DialogBox;
